/**
 * Mobile-safe invite-context persistence (BULLETPROOF EDITION).
 *
 * Now supports a per-invite SECURE TOKEN in addition to the inviter user
 * id. Every invite link generated by InviteModal carries both:
 *   - inviterId  → who invited me (resolves the family graph to attach to)
 *   - token      → the unique invite row in the `invites` table
 *
 * Invite-link URL shapes accepted:
 *   - /invite/<inviterUuid>?t=<token>
 *   - /join/<inviterUuid>?t=<token>
 *
 * Bare inviter UUIDs and tokenless links are not accepted by the
 * governed public invite flow.
 *
 * Storage strategy: write to BOTH localStorage AND sessionStorage so at
 * least one survives mobile in-app webview → external browser handoffs
 * that wipe localStorage. Reads prefer localStorage, fall back to
 * sessionStorage, then to URL parsing.
 */

const KEY = 'kc:pendingInvite';
const BOUND_KEY = 'kc:inviteBindingComplete';
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Token format: 24 hex chars (12 random bytes hex-encoded). Match
// permissively so URL-encoded variants still parse.
const TOKEN_RE = /^[a-zA-Z0-9_-]{16,128}$/;

export type InviteAcceptanceMode =
  | 'SPONSORED_SEAT'
  | 'FAMILY_CONTRIBUTOR'
  | 'SELF_PAID';

export type PendingInviteType =
  | 'FAMILY_PROFILE'
  | 'SPONSORED_SEAT';

export interface PendingInvite {
  inviterId: string;
  inviterName?: string | null;
  /** Per-invite secure token required for governed invite acceptance. */
  token?: string | null;
  inviteType?: PendingInviteType | null;
  acceptanceMode?: InviteAcceptanceMode | null;
  ts?: number;
}

const safeGetItem = (storage: Storage | undefined, key: string): string | null => {
  try { return storage?.getItem(key) ?? null; } catch { return null; }
};
const safeSetItem = (storage: Storage | undefined, key: string, value: string): void => {
  try { storage?.setItem(key, value); } catch { /* ignore */ }
};
const safeRemoveItem = (storage: Storage | undefined, key: string): void => {
  try { storage?.removeItem(key); } catch { /* ignore */ }
};

const localStorageRef = (): Storage | undefined =>
  typeof window !== 'undefined' ? window.localStorage : undefined;
const sessionStorageRef = (): Storage | undefined =>
  typeof window !== 'undefined' ? window.sessionStorage : undefined;

export const saveInviteContext = (ctx: PendingInvite): void => {
  if (!ctx?.inviterId) return;
  const payload = JSON.stringify({
    inviterId: ctx.inviterId,
    inviterName: ctx.inviterName ?? null,
    token: ctx.token ?? null,
    inviteType: ctx.inviteType ?? null,
    acceptanceMode: ctx.acceptanceMode ?? null,
    ts: Date.now(),
  });
  safeSetItem(localStorageRef(), KEY, payload);
  safeSetItem(sessionStorageRef(), KEY, payload);
};

export const getInviteContext = (): PendingInvite | null => {
  for (const storage of [localStorageRef(), sessionStorageRef()]) {
    const raw = safeGetItem(storage, KEY);
    if (!raw) continue;
    try {
      const p = JSON.parse(raw);
      if (p && typeof p === 'object' && typeof p.inviterId === 'string' && p.inviterId) {
        return {
          inviterId: p.inviterId,
          inviterName: typeof p.inviterName === 'string' ? p.inviterName : null,
          token: typeof p.token === 'string' && p.token ? p.token : null,
          inviteType:
            p.inviteType === 'SPONSORED_SEAT'
              ? 'SPONSORED_SEAT'
              : p.inviteType === 'FAMILY_PROFILE'
                ? 'FAMILY_PROFILE'
                : null,
          acceptanceMode:
            p.acceptanceMode === 'SPONSORED_SEAT' ||
            p.acceptanceMode === 'FAMILY_CONTRIBUTOR' ||
            p.acceptanceMode === 'SELF_PAID'
              ? p.acceptanceMode
              : null,
          ts: typeof p.ts === 'number' ? p.ts : undefined,
        };
      }
    } catch { /* parse error — try next storage */ }
  }
  return null;
};

export const clearInviteContext = (): void => {
  safeRemoveItem(localStorageRef(), KEY);
  safeRemoveItem(sessionStorageRef(), KEY);
};

export const markInviteBound = (inviterId: string): void => {
  if (!inviterId) return;
  safeSetItem(localStorageRef(), BOUND_KEY, inviterId);
  safeSetItem(sessionStorageRef(), BOUND_KEY, inviterId);
  safeRemoveItem(localStorageRef(), KEY);
  safeRemoveItem(sessionStorageRef(), KEY);
};

export const isInviteBound = (inviterId: string): boolean => {
  if (!inviterId) return false;
  for (const storage of [localStorageRef(), sessionStorageRef()]) {
    if (safeGetItem(storage, BOUND_KEY) === inviterId) return true;
  }
  return false;
};

/**
 * Reconstruct the pending-invite payload from the URL when storage was
 * wiped. Now ALSO captures `?t=<token>` so the binding pipeline can
 * mark the correct invite row as accepted.
 */
export const restoreInviteContextFromUrl = (): PendingInvite | null => {
  const existing = getInviteContext();
  if (existing) return existing;
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    let inviterId: string | null = null;
    let token: string | null = null;

    // Path-style: /invite/<uuid> or /join/<uuid>
    const parts =
      url.pathname
        .split('/')
        .filter(Boolean);

    const inviteIdx =
      parts.findIndex(
        part =>
          part === 'invite' ||
          part === 'join',
      );

    if (
      inviteIdx >= 0 &&
      parts[inviteIdx + 1] &&
      UUID_RE.test(
        parts[inviteIdx + 1],
      )
    ) {
      inviterId =
        parts[inviteIdx + 1];
    }

    // Token: prefer ?t=, then ?token=
    const tParam = url.searchParams.get('t') || url.searchParams.get('token');
    if (tParam && TOKEN_RE.test(tParam.trim())) {
      token = tParam.trim();
    }

    if (inviterId && token) {
      const ctx: PendingInvite = {
        inviterId,
        inviterName: null,
        token,
        inviteType: null,
        acceptanceMode: null,
        ts: Date.now(),
      };
      saveInviteContext(ctx);
      console.debug('[inviteContext] restored from URL', { inviterId, hasToken: !!token });
      return ctx;
    }
  } catch (e) {
    console.warn('[inviteContext] restoreInviteContextFromUrl failed', e);
  }
  return null;
};
